/**
 * Prompt Editor Module
 * 
 * Form for creating and editing prompts with full text editor and metadata fields.
 */

import logger from '../../core/logger.js';
import { promptsService } from '../../services/prompts-service.js';
import { PromptVersion, PromptStatus } from '../../types/prompt-model.js';
import toast from '../../ui/toast.js';

/**
 * Prompt editor callbacks
 */
interface PromptEditorCallbacks {
  onSave: () => void;
  onCancel: () => void;
}

let callbacks: PromptEditorCallbacks | null = null;

/**
 * Register callbacks
 */
export function registerPromptEditorCallbacks(cbs: PromptEditorCallbacks): void {
  callbacks = cbs;
}

/**
 * Render prompt editor (create or edit mode)
 */
export async function renderPromptEditor(
  container: HTMLElement,
  promptId?: string
): Promise<void> {
  logger.info('admin: Rendering prompt editor', { promptId });
  
  // Clear container
  container.innerHTML = '';
  
  // Load existing prompt if editing
  let prompt: PromptVersion | null = null;
  if (promptId) {
    try {
      prompt = await promptsService.getPrompt(promptId);
    } catch (error) {
      logger.error('admin: Failed to load prompt', { promptId, error });
      toast.error('Failed to load prompt');
      return;
    }
  }
  
  const isEditMode = !!prompt;
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = isEditMode ? 'Edit Prompt' : 'Create New Prompt';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = isEditMode 
    ? `Editing: ${prompt!.title}`
    : 'Create a new prompt for AI content generation';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create form
  const form = document.createElement('form');
  form.className = 'prompt-editor-form';
  form.style.maxWidth = '800px';
  form.style.margin = '0 auto';
  
  // Title
  const titleGroup = createFormGroup('Title', 'title', 'text', prompt?.title || '', 'e.g., Turkish B2 Dialogue Generator');
  form.appendChild(titleGroup);
  
  // Language
  const languageGroup = createSelectGroup(
    'Language',
    'language',
    [
      { value: 'tr', label: 'Turkish' },
      { value: 'de', label: 'German' },
      { value: 'en', label: 'English' }
    ],
    prompt?.language || 'tr'
  );
  form.appendChild(languageGroup);
  
  // Level
  const levelGroup = createSelectGroup(
    'CEFR Level',
    'level',
    [
      { value: 'B2', label: 'B2' },
      { value: 'B1', label: 'B1' }
    ],
    prompt?.level || 'B2'
  );
  form.appendChild(levelGroup);
  
  // Body (prompt text)
  const bodyGroup = createTextareaGroup(
    'Prompt Body',
    'body',
    prompt?.body || '',
    'Enter the full prompt text here...',
    10
  );
  form.appendChild(bodyGroup);
  
  // Tags
  const tagsGroup = createFormGroup(
    'Tags',
    'tags',
    'text',
    prompt?.tags?.join(', ') || '',
    'comma-separated, e.g., dialogue, casual, everyday'
  );
  form.appendChild(tagsGroup);
  
  // Status display (if editing)
  if (isEditMode && prompt) {
    const statusGroup = document.createElement('div');
    statusGroup.className = 'form-group';
    
    const statusLabel = document.createElement('label');
    statusLabel.className = 'form-label';
    statusLabel.textContent = 'Status';
    statusGroup.appendChild(statusLabel);
    
    const statusBadge = document.createElement('span');
    statusBadge.className = 'state-badge';
    statusBadge.textContent = prompt.status.charAt(0).toUpperCase() + prompt.status.slice(1);
    
    if (prompt.status === PromptStatus.Draft) {
      statusBadge.classList.add('state-badge--draft');
    } else if (prompt.status === PromptStatus.Approved) {
      statusBadge.classList.add('state-badge--approved');
    } else {
      statusBadge.classList.add('state-badge--deprecated');
    }
    
    statusGroup.appendChild(statusBadge);
    form.appendChild(statusGroup);
  }
  
  // Action buttons
  const actionsDiv = document.createElement('div');
  actionsDiv.className = 'form-actions';
  actionsDiv.style.display = 'flex';
  actionsDiv.style.gap = '1rem';
  actionsDiv.style.marginTop = '2rem';
  
  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'btn btn--primary';
  saveBtn.textContent = isEditMode ? 'Save Changes' : 'Create Prompt';
  saveBtn.addEventListener('click', async () => {
    await handleSave(form, promptId);
  });
  actionsDiv.appendChild(saveBtn);
  
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onCancel();
  });
  actionsDiv.appendChild(cancelBtn);
  
  form.appendChild(actionsDiv);
  
  container.appendChild(form);
}

/**
 * Create a form group with label and input
 */
function createFormGroup(
  label: string,
  name: string,
  type: string,
  value: string,
  placeholder?: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `prompt-${name}`;
  group.appendChild(labelEl);
  
  const input = document.createElement('input');
  input.type = type;
  input.id = `prompt-${name}`;
  input.name = name;
  input.className = 'form-input';
  input.value = value;
  if (placeholder) input.placeholder = placeholder;
  group.appendChild(input);
  
  return group;
}

/**
 * Create a select group with label and dropdown
 */
function createSelectGroup(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  value: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `prompt-${name}`;
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `prompt-${name}`;
  select.name = name;
  select.className = 'form-input';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.value === value) option.selected = true;
    select.appendChild(option);
  });
  
  group.appendChild(select);
  return group;
}

/**
 * Create a textarea group with label
 */
function createTextareaGroup(
  label: string,
  name: string,
  value: string,
  placeholder?: string,
  rows: number = 4
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `prompt-${name}`;
  group.appendChild(labelEl);
  
  const textarea = document.createElement('textarea');
  textarea.id = `prompt-${name}`;
  textarea.name = name;
  textarea.className = 'form-input';
  textarea.rows = rows;
  textarea.value = value;
  if (placeholder) textarea.placeholder = placeholder;
  group.appendChild(textarea);
  
  return group;
}

/**
 * Handle save action
 */
async function handleSave(form: HTMLFormElement, promptId?: string): Promise<void> {
  try {
    // Gather form data
    const formData = new FormData(form);
    const title = formData.get('title') as string;
    const language = formData.get('language') as 'tr' | 'de' | 'en';
    const level = formData.get('level') as 'B2' | 'B1';
    const body = formData.get('body') as string;
    const tagsRaw = formData.get('tags') as string;
    const tags = tagsRaw ? tagsRaw.split(',').map(t => t.trim()).filter(t => t) : [];
    
    // Validate
    if (!title || !body) {
      toast.error('Title and body are required');
      return;
    }
    
    // Save
    if (promptId) {
      // Update existing
      await promptsService.updatePrompt(promptId, { title, language, level, body, tags });
      toast.success('Prompt updated successfully');
    } else {
      // Create new
      await promptsService.createPrompt({ title, language, level, body, tags });
      toast.success('Prompt created successfully');
    }
    
    // Callback to return to list
    if (callbacks) callbacks.onSave();
    
  } catch (error) {
    logger.error('admin: Failed to save prompt', { error });
    toast.error('Failed to save prompt');
  }
}
